import {
  Button,
  Card,
  Checkbox,
  Flex,
  Heading,
} from '@utilitywarehouse/hearth-react';
import { FieldArray, Formik, useFormikContext } from 'formik';
import type { Ref } from 'react';

import { DialogID, useDialog } from '@components/Dialog';
import { FormikPersist } from '@components/FormikPersist';
import { MarketingConsent } from '@components/MarketingConsent';
import { SAVE_FOR_LATER } from '@constants/formNames';
import { FormSync } from '@features/Remote/Interaction/FormSync';
import { AppointmentParticipantType } from '@generated/gql/graphql';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { BooleanSelection } from '@src/types/index';

import { SaveFormValues } from '../types';
import { EmailFields, MobileFields } from './Fields';
import { KeepInTouchSection } from './KeepInTouchSection';
import './styles.scss';
import { useLoadYourDetailsValues } from './useLoadYourDetailsValues';
import { validateSaveForm } from './validate';

const initialValues: SaveFormValues = {
  emails: [''],
  mobiles: [''],
  sendEmailToPartners: BooleanSelection.No,
  acceptMarketing: BooleanSelection.Yes,
};

interface FormProps {
  isLoading: boolean;
  ref?: Ref<HTMLFormElement>;
}

interface SaveFormProps {
  isLoading: boolean;
  onSubmit: (values: SaveFormValues) => void;
  ref?: Ref<HTMLFormElement>;
}

const Form = ({ isLoading, ref }: FormProps) => {
  useLoadYourDetailsValues();
  const { handleSubmit, isValid, values, setFieldValue } =
    useFormikContext<SaveFormValues>();

  const marketingConsentDialog = useDialog(DialogID.MARKETING_CONSENT);
  const { appointment } = useAppointmentContext();

  const partnerNames =
    appointment?.participants
      .filter(
        (p) =>
          p.type === AppointmentParticipantType.Partner ||
          p.type === AppointmentParticipantType.SupportingPartner
      )
      .map((p) => p.name.split(' ')[0]) ?? [];

  return (
    <>
      <FormSync />
      <FormikPersist name={SAVE_FOR_LATER} />
      <Flex asChild spacing="xl" direction="column">
        <form onSubmit={handleSubmit} ref={ref} noValidate>
          <Card
            direction="column"
            variant="subtle"
            colorScheme="neutralStrong"
            spacing="2xl"
          >
            <Flex direction="column" spacing="xl">
              <Heading size="md">Share an email link</Heading>
              <FieldArray
                name="emails"
                render={(props) => (
                  <EmailFields {...props} isLoading={isLoading} />
                )}
              />
              {appointment && partnerNames.length > 0 && (
                <>
                  <Checkbox
                    data-testid="save-for-later-partner-checkbox"
                    id="save-for-later-partner-checkbox"
                    name="save-for-later-partner-checkbox"
                    label={`Email a copy of my quote to ${partnerNames.join(' and ')}`}
                    onCheckedChange={(checked) =>
                      setFieldValue(
                        'sendEmailToPartners',
                        checked ? BooleanSelection.Yes : BooleanSelection.No
                      )
                    }
                    checked={
                      values.sendEmailToPartners === BooleanSelection.Yes
                    }
                  />
                </>
              )}
            </Flex>
            <Flex direction="column" spacing="xl">
              <Heading size="md">Share an SMS link</Heading>
              <FieldArray
                name="mobiles"
                render={(props) => (
                  <MobileFields {...props} isLoading={isLoading} />
                )}
              />
            </Flex>

            <KeepInTouchSection
              isLoading={isLoading}
              hasChangedPreferences={
                values.acceptMarketing !== initialValues.acceptMarketing
              }
              marketingAccepted={
                values.acceptMarketing === BooleanSelection.Yes
              }
              onDialogOpen={() => marketingConsentDialog.setOpen(true)}
            />
            <MarketingConsent
              content="If you choose not to hear from us, we won’t be able to let you know if your chosen tariffs change, or how you could save more."
              agreeLabel="Keep me up to date with tariff changes and new ways to save"
              declineLabel="Opt me out - I don’t mind missing out on savings"
              name="acceptMarketing"
              onClose={() => marketingConsentDialog.setOpen(false)}
            />
          </Card>
          <Flex
            direction="column"
            alignItems={{
              mobile: 'stretch',
              desktop: 'end',
            }}
          >
            <Button
              type="submit"
              disabled={!isValid}
              data-testid="save-for-later-submit"
            >
              Share my quote
            </Button>
          </Flex>
        </form>
      </Flex>
    </>
  );
};

const SaveForm = ({ onSubmit, isLoading, ref }: SaveFormProps) => (
  <Formik
    validateOnMount
    initialValues={initialValues}
    validate={validateSaveForm}
    onSubmit={onSubmit}
  >
    <Form ref={ref} isLoading={isLoading} />
  </Formik>
);

export { SaveForm };
